import { useMemo } from 'react';
import cn from 'classnames';
import { Popover } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { UsersRequest } from '../../../../store/reducers/UsersSlice';
import { UsersTablet } from '../../../../models/Users/Users';
import styles from './style.module.css';
import { ROLES } from '../../../../components/Users/constants';
import { Clinic } from '../../../../models/MedVox/Clinic';
import { Role } from '../../../../models/Users/Role';
import { EditUserButton } from './EditUserButton';
import sharedCellStyles from '../../../../components/shared/tableCellContent.module.css';
import {
  createServerSortChangeHandler,
  withServerSorter,
} from '../../../../hooks/shared/useServerTableSort';
import {
  usersServerSortDefaults,
  usersSortMap,
} from '../../../../hooks/shared/journalSortMaps';

interface Props {
  onCallRequest: (value: UsersRequest) => void;
  callRequest: UsersRequest;
  clinics?: readonly Clinic[];
}

const CLINICS_POPOVER_TITLE = 'Медицинские организации';

const getOrganizationCountText = (count: number) => {
  const lastTwoDigits = count % 100;
  const lastDigit = count % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return `${count} организаций`;
  }

  if (lastDigit === 1) {
    return `${count} организация`;
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return `${count} организации`;
  }

  return `${count} организаций`;
};

const renderClinicsCellContent = (text: string, isPill = true) => (
  <div className={styles.clinicsCell}>
    <span
      className={cn(
        styles.tabletText,
        isPill && styles.clinicsCellValue,
        sharedCellStyles.textClamp1,
      )}
    >
      {text}
    </span>
  </div>
);

const EMPTY_CLINICS: readonly Clinic[] = [];

export const useUsersColumns = ({
  onCallRequest,
  callRequest,
  clinics: availableClinics = EMPTY_CLINICS,
}: Props) => {
  const baseColumns = useMemo<ColumnsType<UsersTablet>>(
    () => [
      {
        title: <div className={sharedCellStyles.headerCenter}>Логин</div>,
        dataIndex: 'userName',
        width: 100,
        render: (text: string) => (
          <div className={styles.tableCell}>
            <span
              className={styles.tabletText}
              style={{ display: 'block', textAlign: 'center' }}
            >
              {text}
            </span>
          </div>
        ),
      },
      {
        title: <div className={sharedCellStyles.headerCenter}>Роль</div>,
        dataIndex: 'role',
        align: 'left' as const,
        width: 200,
        render: (role: Role) => (
          <div className={styles.tableCell}>
            <span
              className={styles.tabletText}
              style={{ display: 'block', textAlign: 'center' }}
            >
              {ROLES[role.name]}
            </span>
          </div>
        ),
      },
      {
        title: (
          <div className={sharedCellStyles.headerCenter}>
            Медицинские организации
          </div>
        ),
        dataIndex: 'clinics',
        align: 'left' as const,
        width: 260,
        render: (clinics?: Clinic[], record?: UsersTablet) => {
          const clinicItems = clinics || [];
          const clinicNames = clinicItems.map((clinic) => clinic.name);
          const isAdmin = record?.role.name === 'Admin';
          const selectedClinicIds = new Set(
            clinicItems.map((clinic) => clinic.id),
          );
          const hasAllClinics =
            isAdmin ||
            (record?.role.name === 'User' &&
              availableClinics.length > 0 &&
              availableClinics.every((clinic) =>
                selectedClinicIds.has(clinic.id),
              ));
          const hasPopoverClinics = !hasAllClinics && clinicNames.length > 0;
          const text = hasAllClinics
            ? 'Все'
            : clinicNames.length === 0
              ? '—'
              : getOrganizationCountText(clinicNames.length);

          if (!hasPopoverClinics) {
            return renderClinicsCellContent(text, false);
          }

          return (
            <Popover
              content={
                <div className={styles.clinicsPopoverList}>
                  {clinicItems.map((clinic) => (
                    <div className={styles.clinicsPopoverItem} key={clinic.id}>
                      {clinic.name}
                    </div>
                  ))}
                </div>
              }
              title={CLINICS_POPOVER_TITLE}
              trigger={['hover', 'focus']}
            >
              {renderClinicsCellContent(text)}
            </Popover>
          );
        },
      },
      {
        key: 'createDateTime',
        title: (
          <div className={sharedCellStyles.headerCenter}>Дата создания</div>
        ),
        dataIndex: 'date',
        width: 120,
        render: (text: string) => (
          <div className={styles.tableCell} data-group="usersDate">
            <span
              className={styles.tabletText}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </div>
        ),
      },
      {
        title: <div className={sharedCellStyles.headerCenter}>Статус</div>,
        dataIndex: 'isLockout',
        width: 150,
        render: (value: boolean) => {
          const text = value ? 'Заблокирован' : 'Активен';

          return (
            <div
              className={styles.tableCell}
              style={{ display: 'block', textAlign: 'center' }}
            >
              <span
                className={cn(
                  styles.tabletBadge,
                  value ? styles.tabletBadgeRed : styles.tabletBadgeGreen,
                )}
              >
                {text}
              </span>
            </div>
          );
        },
      },
      {
        dataIndex: 'actions',
        align: 'left' as const,
        width: 40,
        render: (_: any, record: UsersTablet) => (
          <EditUserButton record={record} callRequest={callRequest} />
        ),
      },
    ],
    [callRequest, availableClinics],
  );

  const COLUMNS = useMemo(
    () => withServerSorter(baseColumns, callRequest, usersSortMap),
    [baseColumns, callRequest],
  );

  const onTableChange = useMemo(
    () =>
      createServerSortChangeHandler<UsersTablet, UsersRequest>(
        callRequest,
        usersSortMap,
        onCallRequest,
        usersServerSortDefaults,
      ),
    [callRequest, onCallRequest],
  );

  return { COLUMNS, onTableChange };
};
